import { motion } from "framer-motion";
import { Users, Briefcase, GraduationCap } from "lucide-react";
import SectionHeader from "./SectionHeader";

const IMG =
  "https://images.pexels.com/photos/36752220/pexels-photo-36752220.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PILLARS = [
  {
    icon: Users,
    title: "Youth Delegates Assembly",
    copy: "Elected young delegates from all 45 states convene to legislate movement, trade, and opportunity for the continent's youth.",
  },
  {
    icon: Briefcase,
    title: "Cross-Border Trade Grants",
    copy: "Young entrepreneurs receive Union-backed support to build businesses that trade freely across every member state.",
  },
  {
    icon: GraduationCap,
    title: "Student Exchange Corridors",
    copy: "Study in Dakar, work in Nairobi, build in Kigali — one passport carries a generation across one continent.",
  },
];

export default function YouthParliament() {
  return (
    <section id="parliament" className="py-20 lg:py-32" data-testid="youth-parliament-section">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute inset-0 translate-x-4 translate-y-4 rounded-xl border border-[#00A651]/30" />
            <img
              src={IMG}
              alt="Young African leaders of the Youth Parliament standing together"
              className="relative rounded-xl object-cover aspect-[4/3] w-full"
              loading="lazy"
            />
            <div className="absolute -bottom-6 left-6 rounded-lg border border-[#00A651]/25 bg-[#121519]/95 backdrop-blur px-5 py-4" data-testid="parliament-badge">
              <p className="font-display font-extrabold text-[#00A651] text-lg leading-none">AYP</p>
              <p className="font-official text-[10px] tracking-[0.2em] uppercase text-[#8A99AD] mt-1.5">
                African Youth Parliament
              </p>
            </div>
          </motion.div>

          <div>
            <SectionHeader
              kicker="The Governing Generation"
              title="African Youth Parliament of the United States of Africa"
              copy="Not a council of tomorrow — a parliament of today. The young people of the Union write the laws of a borderless continent and carry its passport across every region."
            />
            <div className="mt-10 space-y-5">
              {PILLARS.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  className="flex gap-5 items-start rounded-xl border border-[#00A651]/12 bg-[#121519] p-5 hover:border-[#00A651]/40 transition-colors duration-300"
                  data-testid={`parliament-pillar-${i + 1}`}
                >
                  <div className="shrink-0 w-11 h-11 rounded-full bg-[#00A651]/10 border border-[#00A651]/30 flex items-center justify-center text-[#00A651]">
                    <p.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-base text-[#F7F5F0]">{p.title}</h3>
                    <p className="text-sm text-[#8A99AD] mt-1.5 leading-relaxed">{p.copy}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
